import type { TFn } from "./i18nTypes";

// THE relative age of something, in words. "today", "3 d ago", "6 sett. fa".
//
// ⭐ It exists because `CN-relative-age` counted THREE hand-rolled copies of
// `n === 0 ? today : t(daysAgo, { n })` — `Coach/AthleteCardRow.tsx`,
// `Progress/ExerciseFrequencyPanel.tsx`, `Home/RecentPrsStrip.tsx` — each with
// its OWN i18n key, so one sentence cost 3 key families × 5 locales = 15
// translations. The entry's rule for whoever came next was blunt: build one
// `formatRelativeAge(t, days)` on a single key family, and ⛔ do not add the
// fourth key. LG-49's history pill is that fourth caller, so this is it.
//
// ⚠️ Stalest exercises passes `maxUnit: "weeks"` (Antenore, 29/09, PG-64) so
// its rows stay distinguishable; the rest of the app keeps the default scale.
//
// ⛔ Why it escalates at all: the pill's whole job is to be read at a glance
// mid-set. "182 d ago" is a number the athlete has to divide; "6 mesi fa" is
// the fact itself. The unit changes, the meaning does not.

/** Below this, days are still the unit an athlete thinks in. */
const WEEKS_FROM_DAYS = 14;
/** Above this, weeks stop being countable at a glance. */
const MONTHS_FROM_DAYS = 60;
const DAYS_PER_WEEK = 7;
/** Average, not 30: the pill states an age, it does not do calendar maths. */
const DAYS_PER_MONTH = 30.44;

/**
 * @param days Whole days since the event — `shared`'s `daysSince()` computes
 *   it, this only words it. Null (never happened) returns null: "never" is a
 *   different sentence and belongs to the caller, which knows what the thing
 *   IS ([[feedback_empty_vs_unresolved_collection]]).
 * @param opts.maxUnit "weeks" never escalates to months (opt-in per callsite).
 */
export function formatRelativeAge(
  t: TFn,
  days: number | null,
  opts?: { maxUnit?: "weeks" },
): string | null {
  if (days === null || !Number.isFinite(days)) return null;
  const d = Math.max(0, Math.floor(days));

  if (d === 0) return t("common.relativeAge.today", { defaultValue: "today" });

  if (d < WEEKS_FROM_DAYS)
    return t("common.relativeAge.days", {
      defaultValue: "{{count}}d ago",
      count: d,
    });

  // `maxUnit: "weeks"` caps the scale for a list whose rows must stay
  // distinguishable ("13w" vs "16w", not eight identical "3mo").
  if (d < MONTHS_FROM_DAYS || opts?.maxUnit === "weeks")
    return t("common.relativeAge.weeks", {
      defaultValue: "{{count}}w ago",
      count: Math.round(d / DAYS_PER_WEEK),
    });

  return t("common.relativeAge.months", {
    defaultValue: "{{count}}mo ago",
    count: Math.max(1, Math.round(d / DAYS_PER_MONTH)),
  });
}
